import { Form, Input, Popover } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { UnderlineText } from 'components/RevxTypography';
import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { cloneDeep, sum } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { MIN_PCT, MIN_PRICE } from 'utils/constants';
import { ComparisonMoney } from './ComparisonMoney';
import './PriceOverride.less';
import { ISourceComparisonItem } from './SourceComparisonItem';

interface Ipayload {
  revxPrintingCost?: number | string;
  revxMarkupPct?: number | string;
  revxMarkup?: number | string;
  revxFreightCost?: number | string;
  revxShippingMarkupPct?: number | string;
  revxShippingMarkup?: number | string;
}

export const PriceOverride = (props: ISourceComparisonItem) => {
  const {
    jobEstimatorItem,
    setJobEstimatorLoading,
    jobEstimator,
    choiceSerialNumber,
    updateComparison,
  } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [totalCharge, setTotalCharge] = useState<number>(
    jobEstimatorItem.comparator?.totalCharge
  );
  const [payload, setPayload] = useState<Ipayload>();
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (jobEstimatorItem.comparator) {
      form.setFieldsValue(jobEstimatorItem.comparator);
      setTotalCharge(jobEstimatorItem.comparator?.totalCharge);
      const comparatorPayload: Ipayload = cloneDeep(
        jobEstimatorItem.comparator
      );
      delete comparatorPayload.revxMarkup;
      delete comparatorPayload.revxShippingMarkup;
      setPayload({
        revxPrintingCost: comparatorPayload.revxPrintingCost,
        revxMarkupPct: comparatorPayload.revxMarkupPct,
        revxMarkup: comparatorPayload.revxMarkup,
        revxFreightCost: comparatorPayload.revxFreightCost,
        revxShippingMarkupPct: comparatorPayload.revxShippingMarkupPct,
        revxShippingMarkup: comparatorPayload.revxShippingMarkup,
      });
    }
  }, [form, jobEstimatorItem, visible]);
  const totalChargeHandler = async () => {
    try {
      const allValues = await form.getFieldsValue();
      setTotalCharge(
        sum([
          sum([+allValues.revxPrintingCost, +allValues.revxMarkup]) *
            (1 + (jobEstimatorItem.comparator.outsourceMarkupPct || 0) / 100),
          sum([+allValues.revxFreightCost, +allValues.revxShippingMarkup]) *
            (1 +
              (jobEstimatorItem.comparator.outsourceShippingMarkupPct || 0) /
                100),
        ])
      );
    } catch {}
  };
  const freightDisabled = useMemo(() => {
    return !jobEstimatorItem.comparator.shippings.filter(
      (item) =>
        item?.shippingInfo?.accountType !==
          ShippingAccountTypeEnum.ORGANIZATION &&
        item?.shippingInfo?.carrierType !== CarrierTypeEnum.Nonstandard
    ).length;
  }, [jobEstimatorItem.comparator.shippings]);
  const content = (
    <>
      <RevxFlexLayout justify="space-between" className="price-override-header">
        <div>{jobEstimatorItem.orgName || 'Printopia'}</div>
      </RevxFlexLayout>
      <RevxFlexLayout className="price-override-table">
        <div className="price-override-table-item price-override-table-item-label">
          <div>Printing Costs</div>
          <div>Profit Margin/Discount</div>
          <div>Freight Method</div>
          <div>Shipping Costs</div>
          <div>Shipping Markup/Discount</div>
          <div>Job Net Total</div>
        </div>
        <Form className="price-override-table-item" form={form}>
          <div>
            <Form.Item noStyle name="revxPrintingCost">
              <RevxInputNumber
                onBlur={(e) => {
                  if (Number(payload?.revxPrintingCost) === +e) return;
                  const data: Ipayload = form.getFieldsValue();
                  form.setFieldsValue({
                    ...data,
                    revxMarkup: +(
                      Number(
                        +e / (1 - Number(data?.revxMarkupPct || 0) / 100)
                      ) - +e
                    ).toFixed(2),
                  });
                  setPayload((value) => {
                    return {
                      ...value,
                      revxPrintingCost: e,
                    };
                  });
                  totalChargeHandler();
                }}
                prefix="$"
                min={MIN_PRICE}
                precision={2}
              />
            </Form.Item>
          </div>
          <RevxFlexLayout>
            <Form.Item noStyle name="revxMarkupPct">
              <RevxInputNumber
                onBlur={(e) => {
                  if (Number(payload?.revxMarkupPct) === +e) return;
                  const data: Ipayload = form.getFieldsValue();
                  form.setFieldsValue({
                    ...data,
                    revxMarkup: +(
                      Number(Number(data.revxPrintingCost) / (1 - +e / 100)) -
                      Number(data.revxPrintingCost)
                    ).toFixed(2),
                  });
                  delete data.revxMarkup;
                  setPayload((value) => {
                    delete value?.revxMarkup;
                    return {
                      ...value,
                      revxMarkupPct: e,
                    };
                  });
                  totalChargeHandler();
                }}
                min={MIN_PCT}
                precision={2}
                suffix="%"
                disabled={!form.getFieldValue('revxPrintingCost')}
              />
            </Form.Item>
            <Form.Item noStyle name="revxMarkup">
              <RevxInputNumber
                onBlur={(e) => {
                  if (Number(payload?.revxMarkup) === +e) return;
                  const data: Ipayload = form.getFieldsValue();
                  form.setFieldsValue({
                    ...data,
                    revxMarkupPct: Number(data.revxPrintingCost)
                      ? (
                          (+e / (Number(data.revxPrintingCost) + Number(e))) *
                          100
                        )?.toFixed(2)
                      : 0,
                  });
                  totalChargeHandler();
                  setPayload((value) => {
                    delete value?.revxMarkupPct;
                    return {
                      ...value,
                      revxMarkup: e,
                    };
                  });
                }}
                prefix="$"
                precision={2}
                disabled={!form.getFieldValue('revxPrintingCost')}
              />
            </Form.Item>
          </RevxFlexLayout>
          <div>
            {jobEstimatorItem.comparator?.shippings.length === 1 ? (
              <Input
                value={
                  jobEstimatorItem.comparator?.shippings[0].shippingInfo
                    .carrierService
                }
                disabled
              />
            ) : (
              <Input
                value={`Split Shipment (${jobEstimatorItem.comparator?.shippings.length})`}
                disabled
              />
            )}
          </div>
          <div>
            <Form.Item noStyle name="revxFreightCost">
              <RevxInputNumber
                disabled={freightDisabled}
                onBlur={(e) => {
                  if (Number(payload?.revxFreightCost) === +e) return;
                  const data: Ipayload = form.getFieldsValue();
                  form.setFieldsValue({
                    ...data,
                    revxShippingMarkup: (
                      +e *
                      (Number(data?.revxShippingMarkupPct) / 100)
                    ).toFixed(2),
                  });
                  totalChargeHandler();
                  setPayload((value) => {
                    return {
                      ...value,
                      revxFreightCost: e,
                    };
                  });
                }}
                prefix="$"
                min={0}
                precision={2}
              />
            </Form.Item>
          </div>
          <RevxFlexLayout>
            <Form.Item noStyle name="revxShippingMarkupPct">
              <RevxInputNumber
                onBlur={(e) => {
                  if (Number(payload?.revxShippingMarkupPct) === +e) return;
                  const data: Ipayload = form.getFieldsValue();
                  form.setFieldsValue({
                    ...data,
                    revxShippingMarkup: (
                      Number(data.revxFreightCost) *
                      (+e / 100)
                    ).toFixed(2),
                  });
                  setPayload((value) => {
                    delete value?.revxShippingMarkup;
                    return {
                      ...value,
                      revxShippingMarkupPct: e,
                    };
                  });
                  totalChargeHandler();
                }}
                min={MIN_PCT}
                precision={2}
                suffix="%"
                disabled={
                  !form.getFieldValue('revxFreightCost') || freightDisabled
                }
              />
            </Form.Item>
            <Form.Item noStyle name="revxShippingMarkup">
              <RevxInputNumber
                onBlur={(e) => {
                  if (Number(payload?.revxShippingMarkup) === +e) return;
                  const data: Ipayload = form.getFieldsValue();
                  form.setFieldsValue({
                    ...data,
                    revxShippingMarkupPct: (
                      (+e / Number(data.revxFreightCost)) *
                      100
                    ).toFixed(2),
                  });
                  setPayload((value) => {
                    delete value?.revxShippingMarkupPct;
                    return {
                      ...value,
                      revxShippingMarkup: e,
                    };
                  });
                  totalChargeHandler();
                }}
                prefix="$"
                precision={2}
                disabled={
                  !form.getFieldValue('revxFreightCost') || freightDisabled
                }
              />
            </Form.Item>
          </RevxFlexLayout>
          <div>
            <ComparisonMoney value={totalCharge} />
          </div>
        </Form>
      </RevxFlexLayout>
      <RevxFlexLayout justify="justify-end" className="price-override-footer">
        <RevxButton
          brandGreen
          type="ghost"
          onClick={() => {
            setVisible(false);
          }}
        >
          Cancel
        </RevxButton>
        <RevxButton
          brandGreen
          type="primary"
          onClick={async () => {
            try {
              setJobEstimatorLoading(true);
              const res = await dispatch.jobEstimator.adjustMarkup({
                jobConfigId: jobEstimator?.jobConfigId || '',
                quantityItemSerialNumber: choiceSerialNumber || 0,
                comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                ...payload,
              });
              updateComparison(res);
              setVisible(false);
            } catch {
              setJobEstimatorLoading(false);
            }
          }}
        >
          Confirm
        </RevxButton>
      </RevxFlexLayout>
    </>
  );
  return (
    <>
      <Popover
        content={content}
        visible={visible}
        overlayClassName="price-override"
        trigger="click"
        destroyTooltipOnHide={true}
        onVisibleChange={(e) => setVisible(e)}
        getPopupContainer={(e) => {
          return e as HTMLElement;
        }}
      >
        <UnderlineText color="blue">
          <Trans i18nKey="jobConfig.editPrice"></Trans>
        </UnderlineText>
      </Popover>
    </>
  );
};
